import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import NewProjectContext from "../../contexts/NewProjectContext";
import Button from "../common/Button";
import _ from "lodash";
import { ObjectId } from "bson";

import { ReactComponent as ArrowRightSvg } from "../../assets/icons/arrow-right.svg";
import { ReactComponent as ArrowLeftSvg } from "../../assets/icons/arrow-left.svg";
import useWorks from "../../hooks/useWorks";
import { PAGE_STATE, langComponent } from "../../constants";
import AppContext from "../../AppContext";
import useTendering from "../../hooks/useTendering";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";

const HOTSPOT_TYPE = {
  WALK_WAY: 1,
  INFO: 2,
  VOICE_MEMO: 3,
  PICTURE: 4,
};

const Summary = (props) => {
  const { getLang, designStyles, user } = useContext(AppContext);
  const Translation = getLang(langComponent.project);
  const TranslationCommon = getLang(langComponent.common);
  const TranslationWorkType = getLang(langComponent.workType);
  const TranslationSpaceType = getLang(langComponent.spaceType);
  const TranslationStyleType = getLang(langComponent.styleType);
  const { details, goBackward, onSuccess, edit } =
    useContext(NewProjectContext);
  const {
    createWork,
    uploadWorkPanoramaImage,
    uploadPanoramaBanner,
    patchHotspot,
    uploadBanner,
    uploadImages,
    uploadFloorPlans,
    uploadHotspotFile,
    uploadHotspotIcon,
    uploadWorkWatermark,
    patchWork,
  } = useWorks();
  const { getSignedUrl } = useTendering();
  const [uploadingState, setUploadingState] = useState(PAGE_STATE.IDLE);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const handleReturn = () => {
    goBackward();
  };

  /**
   * Upload image and return object for saving
   * @returns
   */
  const _uploadImage = async (key, file) => {
    // * Upload original
    const originalKey = key;

    const originalSignedUrl = await getSignedUrl(originalKey, file.type);

    const uploadOriginalResponse = await fetch(originalSignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: 0,
      },
      body: file,
    });

    if (uploadOriginalResponse.status !== 200) {
      toast.error("Unable to upload image");
      return;
    }

    return originalKey;
  };

  const savePanoramaProject = async (draft = false) => {
    const {
      info: {
        name,
        desc,
        price,
        area,
        program,
        renderer,
        work,
        space,
        style,
        location,
      },
      images,
      panoramaImages,
      panoramaCovers,
      scenes,
      cover,
      type,
      music,
      cycles,
      floorPlans,
      floorPlanNames,
      materialGroups,
      watermark,
    } = details;

    const workId = edit ?? new ObjectId();

    // setUploadingState(PAGE_STATE.LOADING);

    // * Validate detail data
    if (panoramaImages.length !== panoramaCovers.length) {
      alert("Panorama images and banners are not aligned");
      return;
    }

    // * Map scenes, cycles with ObjectId
    const _cycles = _.cloneDeep(cycles || []);

    let panoramaScenes = [];

    for (let i = 0; i < scenes.length; i++) {
      const scene = scenes[i];

      const imageId = new ObjectId();

      // * Mutate cycle
      for (const cycle of _cycles) {
        if (cycle.day === scene.imageName) cycle.day = imageId;
        if (cycle.night === scene.imageName) cycle.night = imageId;
      }

      // * Upload image
      const imageKey = `works/${workId}/panoramas/images/${imageId}/image`;
      await _uploadImage(imageKey, panoramaImages[i]);

      // * Compress and upload
      const compressedFile = await imageCompression(panoramaImages[i], {
        maxSizeMB: 2,
        maxWidthOrHeight: 2880,
        useWebWorker: true,
      });

      const compressedKey = `works/${workId}/panoramas/images/${imageId}/compressed`;
      await _uploadImage(compressedKey, compressedFile);

      // * Upload banner
      const bannerKey = `works/${workId}/panoramas/images/${imageId}/cover`;
      await _uploadImage(bannerKey, panoramaCovers[i]);

      const _hotspots = [];

      for (const hotspot of scene.hotspots) {
        const hotspotId = new ObjectId();

        // * Upload custom icon
        let customIcon = {};
        if (hotspot.type === HOTSPOT_TYPE.WALK_WAY && hotspot.customIcon) {
          const iconKey = `works/${workId}/hotspots/${hotspotId}/icons/${new ObjectId()}`;
          await _uploadImage(iconKey, hotspot.customIcon);

          customIcon = {
            personal: {
              file: iconKey,
              name: hotspot.customIcon.name,
              mimeType: hotspot.customIcon.type,
            },
          };
        }

        // * Upload file
        let iconFile = {};
        if (
          (hotspot.type === HOTSPOT_TYPE.VOICE_MEMO ||
            hotspot.type === HOTSPOT_TYPE.PICTURE) &&
          hotspot.file
        ) {
          const fileKey = `works/${workId}/hotspots/${hotspotId}/files/${new ObjectId()}`;
          await _uploadImage(fileKey, hotspot.file);

          iconFile = {
            file: {
              file: fileKey,
              name: hotspot.file.name,
              mimeType: hotspot.file.type,
            },
          };
        }

        _hotspots.push({
          _id: hotspotId,
          type: hotspot.type,
          tooltip: {
            title: hotspot.title,
            desc: hotspot.desc,
          },
          pitch: hotspot.pitch,
          yaw: hotspot.yaw,
          icon: {
            internal: hotspot.icon,
            ...customIcon,
          },
          ...iconFile,
        });
      }

      panoramaScenes.push({
        _id: imageId,
        imageName: scene.imageName, // ! Temporarily, will not be saved in the database
        name: scene.name || "null",
        isMain: scene.imageIndex === 0,
        initPitch: 0,
        initYaw: 0,
        banner: bannerKey,
        image: imageKey,
        compressed: compressedKey,
        hotspots: _hotspots,
      });
    }

    // * Update hotspot panorama reference
    for (let i = 0; i < panoramaScenes.length; i++) {
      const ps = panoramaScenes[i];

      for (let j = 0; j < ps.hotspots.length; j++) {
        const hotspot = ps.hotspots[j];

        if (hotspot.type === HOTSPOT_TYPE.WALK_WAY) {
          const panoRef = panoramaScenes.find(
            (p) => p.name === hotspot.tooltip.title
          );
          panoramaScenes[i].hotspots[j].panorama = panoRef._id;
        }
      }
    }

    const _floorPlans = _.cloneDeep(floorPlans).map((f) => {
      return {
        _id: new ObjectId(),
        type: "Test",
        name: floorPlanNames.find((fpn) => fpn.fileName === f.name)?.name,
      };
    });

    const _materials = _.cloneDeep(materialGroups).map((group) => {
      return {
        _id: new ObjectId(),
        panoramas: group.map((m) => ({
          type: m.type,
          panorama: panoramaScenes.find((scene) => scene.imageName === m.to)
            ._id,
        })),
      };
    });

    const PROGRAMS = {
      sketchUp: 1,
      "3dsMax": 2,
      revit: 3,
      cinema4D: 4,
      unreal: 5,
      rhino: 6,
      autocad: 7,
      archicad: 8,
    };

    const RENDERERS = {
      vray: 1,
      corona: 2,
      lumion: 3,
      enscape: 4,
      d5render: 5,
    };

    let _work = undefined;

    // * Upload banner
    const bannerKey = `works/${workId}/banners/${new ObjectId()}`;
    await _uploadImage(bannerKey, cover);

    if (!edit) {
      _work = await createWork({
        _id: workId,
        banner: bannerKey,
        name,
        desc,
        price: +(price || 0),
        size: +area,
        levels: 1,
        type: 1,
        designType: {
          work,
          space,
          style: style,
        },
        panorama: {
          images: panoramaScenes,
          cycles: _cycles,
          materials: _materials,
        },
        floorPlans: _floorPlans,
        program: PROGRAMS[program],
        renderer: RENDERERS[renderer],
        music: music,
        company: user.company,
        location,
        active: !draft,
        deleted: true,
      });
    }

    // * Patch instead of create if editing
    if (edit) {
      _work = await patchWork(edit, {
        name,
        desc,
        price: +(price || 0),
        size: +area,
        levels: 1,
        type: 1,
        designType: {
          work,
          space,
          style: style,
        },
        panorama: {
          images: panoramaScenes,
          cycles: _cycles,
          materials: _materials,
        },
        floorPlans: _floorPlans,
        program: PROGRAMS[program],
        renderer: RENDERERS[renderer],
        music: music,
        company: user.company,
        location,
        active: !draft,
      });
    }

    // * Link panorama to hotspots
    const updateHotspotPanoramaRequests = [];

    // * Patch hotspot, upload panoramas & banner
    const requests = [...updateHotspotPanoramaRequests];

    if (type !== 1) {
      requests.push(uploadImages(_work._id, images));
    }

    // * Upload floor plans if exists
    for (let i = 0; i < _floorPlans.length; i++) {
      requests.push(
        uploadFloorPlans(_work._id, _floorPlans[i]._id, floorPlans[i])
      );
    }

    // * Upload watermark if exists
    if (watermark) {
      requests.push(uploadWorkWatermark(_work._id, watermark));
    }

    await Promise.allSettled(requests);

    await patchWork(_work._id, {
      deleted: false,
    });

    // console.log("Success!");

    onSuccess(_work._id);
  };

  const saveImagesProject = async (draft = false) => {
    const {
      info: {
        name,
        desc,
        price,
        area,
        program,
        renderer,
        work,
        space,
        style,
        location,
      },
      images,
    } = details;

    setUploadingState(PAGE_STATE.LOADING);

    const PROGRAMS = {
      sketchUp: 1,
      "3dsMax": 2,
      revit: 3,
      cinema4D: 4,
      unreal: 5,
      rhino: 6,
      autocad: 7,
      archicad: 8,
    };

    const RENDERERS = {
      vray: 1,
      corona: 2,
      lumion: 3,
      enscape: 4,
      d5render: 5,
    };

    let _work = undefined;

    if (!edit) {
      _work = await createWork({
        name,
        desc,
        price: +(price || 0),
        size: +area,
        levels: 1,
        type: 3,
        designType: {
          work,
          space,
          style: style,
        },
        program: PROGRAMS[program],
        renderer: RENDERERS[renderer],
        company: user.company,
        location,
        active: !draft,
        deleted: true,
      });
    }

    if (edit) {
      _work = await patchWork(edit, {
        name,
        desc,
        price: +(price || 0),
        size: +area,
        levels: 1,
        type: 3,
        designType: {
          work,
          space,
          style: style,
        },
        program: PROGRAMS[program],
        renderer: RENDERERS[renderer],
        company: user.company,
        location,
        active: !draft,
        deleted: true,
      });
    }

    // * Patch hotspot, upload panoramas & banner
    const requests = [
      uploadBanner(_work._id, cover),
      uploadImages(_work._id, images),
    ];

    const promiseResponse = await Promise.allSettled(requests);

    await patchWork(_work._id, { deleted: false });

    onSuccess(_work._id);
  };

  const handleDraftContinue = () => {
    if (type === "panorama") {
      savePanoramaProject(true);
      return;
    }

    saveImagesProject(true);
  };

  const handleContinue = () => {
    if (type === "panorama") {
      savePanoramaProject();
      return;
    }

    saveImagesProject();
  };

  const {
    info: { name, desc, price, area, program, renderer, work, space, style },
    panoramaImages,
    images,
    cover,
    type,
  } = details;

  const { works, spaces, styles } = designStyles;

  let galleryImages =
    type === "panorama" ? [cover, ...panoramaImages] : [cover, ...images];

  return (
    <div className="new-project-summary">
      <div className="new-project-summary-cover">
        <img
          className="new-project-summary-cover__image"
          alt="banner"
          src={URL.createObjectURL(galleryImages[selectedImageIndex])}
        />
      </div>
      <div className="new-project-summary-gallery-scroller">
        <div className="new-project-summary-gallery">
          {galleryImages.map((image, index) => {
            const _src = URL.createObjectURL(image);
            return (
              <div
                className="new-project-summary-gallery-image"
                onClick={() => setSelectedImageIndex(index)}
              >
                <img
                  className="new-project-summary-gallery-image__preview"
                  alt="preview image"
                  src={_src}
                />
                {index === 0 && (
                  <span className="new-project-summary-gallery-image__cover-tag">
                    Cover
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="new-project-summary-categories">
        {area && area > 0 ? (
          <div className="new-project-summary-categories__category">
            {(+area).toLocaleString()} {TranslationCommon("sq.m.")}
          </div>
        ) : (
          <></>
        )}
        <div className="new-project-summary-categories__category">
          {TranslationWorkType(
            works.find((w) => w.value === work)?.name || work
          )}
        </div>
        <div className="new-project-summary-categories__category">
          {TranslationSpaceType(
            spaces.find((w) => w.value === space)?.name || space
          )}
        </div>
        {style.map((s) => {
          return (
            <div className="new-project-summary-categories__category">
              {TranslationStyleType(
                styles.find((w) => w.value === s)?.name || s
              )}
            </div>
          );
        })}
      </div>
      <h1 className="new-project-summary__name">{name}</h1>
      <p className="new-project-summary__desc">{desc}</p>
      {price && (
        <>
          <span className="new-project-summary-price">
            {(+price).toLocaleString()}{" "}
            <span className="new-project-summary-price__currency">THB</span>
          </span>
          {type === 1 && (
            <span className="new-project-summary-area-price">
              {`(${(+price / +area).toLocaleString()} THB per sq.m.)`}
            </span>
          )}
        </>
      )}
      <div className="new-project-summary-footer">
        {uploadingState === PAGE_STATE.LOADING ? (
          <>Uploading, please don't close this window</>
        ) : (
          <>
            <Button
              label={TranslationCommon("Return")}
              variant={"secondary"}
              leadingIcon={<ArrowLeftSvg />}
              onClick={handleReturn}
            />
            <div
              className="new-project-summary-footer__right"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <Button
                label={Translation("Save draft")}
                variant={"tertiary"}
                onClick={handleDraftContinue}
              />
              <Button
                label={
                  edit
                    ? Translation("Update Project")
                    : Translation("Share Project")
                }
                trailingIcon={<ArrowRightSvg />}
                onClick={handleContinue}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

Summary.propTypes = {};

export default Summary;
